// src/features/records/components/CalendarView/utils/dateHelpers.js
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek,
  startOfDay,
  addDays,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  parseISO,
  isValid
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Timestamp } from 'firebase/firestore';
import {
  buildDateKey,
  resolveLocale,
  resolveTimeZone,
  coerceDateValue,
} from '../../../../../utils/dateUtils';

const translate = (t, key, options = {}, fallback = key) => {
  if (typeof t === 'function') {
    return t(key, { ...options, defaultValue: fallback });
  }
  return fallback;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const EN_WEEKDAY_TO_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const formatWithIntl = (date, locale, options, timeZone) => {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
};

const isSameDayInZone = (left, right, { locale, timeZone } = {}) => {
  if (!left || !right) return false;
  const leftKey = buildDateKey(left, { locale, timeZone });
  const rightKey = buildDateKey(right, { locale, timeZone });
  if (!leftKey || !rightKey) return false;
  return leftKey === rightKey;
};

/**
 * Get the calendar days for a month view (includes previous/next month days)
 * @param {Date} date - Any date in the target month
 * @returns {Array<Date>} Array of dates for the calendar grid
 */
export const getCalendarDays = (date) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  
  const days = [];
  let currentDate = calendarStart;
  
  while (currentDate <= calendarEnd) {
    days.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }
  
  return days;
};

/**
 * Get the [start, end) range that matches the calendar grid for the month
 * @param {Date} date - Any date in the target month
 * @param {number} weekStartsOn - 0 = Sunday, 1 = Monday
 * @returns {{start: Date, end: Date}} Range covering the calendar grid
 */
export const getCalendarGridRange = (date, weekStartsOn = 0) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const gridStart = startOfWeek(monthStart, { weekStartsOn });
  const gridEndInclusive = endOfWeek(monthEnd, { weekStartsOn });
  const gridEndExclusive = addDays(startOfDay(gridEndInclusive), 1);

  return { start: gridStart, end: gridEndExclusive };
};

/**
 * Convert Firestore Timestamp to Date
 * @param {Timestamp|Date|string} dateValue - Date value to convert
 * @returns {Date} JavaScript Date object
 */
export const toDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Check if it's a Firestore Timestamp object or similar structure
  if (dateValue instanceof Timestamp) {
    return dateValue.toDate();
  }
  
  // Check if it has Timestamp-like properties (seconds and nanoseconds)
  if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue && 'nanoseconds' in dateValue) {
    // Create a new Timestamp from the properties
    return new Timestamp(dateValue.seconds, dateValue.nanoseconds).toDate();
  }
  
  if (dateValue instanceof Date) {
    return dateValue;
  }
  
  if (typeof dateValue === 'string') {
    const parsed = parseISO(dateValue);
    return isValid(parsed) ? parsed : null;
  }
  
  return null;
};

/**
 * Get the display date for a record (considering timezone)
 * @param {Object} record - Record with date attributes
 * @returns {Date|null} Display date in local timezone
 */
export const getRecordDisplayDate = (record) => {
  if (!record?.attributes?.date?.start) return null;
  
  const startDate = toDate(record.attributes.date.start);
  if (!startDate) return null;
  
  // If timezone is specified, convert to that timezone for display
  if (record.attributes.date.timezone) {
    return toZonedTime(startDate, record.attributes.date.timezone);
  }
  
  return startDate;
};

/**
 * Check if a record occurs on a specific date
 * @param {Object} record - Record with date attributes
 * @param {Date} date - Date to check
 * @returns {boolean} True if record occurs on this date
 */
export const isRecordOnDate = (record, date) => {
  const recordDate = getRecordDisplayDate(record);
  if (!recordDate) return false;
  
  // For all-day events or events without end date, just check if it's the same day
  if (record.attributes.date.isAllDay || !record.attributes.date.end) {
    return isSameDay(recordDate, date);
  }
  
  // Get the end date
  const endDate = toDate(record.attributes.date.end);
  if (!endDate) return isSameDay(recordDate, date);
  
  // Check if this is a same-day event (even if it has different times)
  if (isSameDay(recordDate, endDate)) {
    return isSameDay(recordDate, date);
  }
  
  // For true multi-day events, check if the date falls within the range
  // We use startOfDay to compare just the dates, not the times
  const dayStart = startOfDay(date);
  const eventStart = startOfDay(recordDate);
  const eventEnd = startOfDay(endDate);
  
  return dayStart >= eventStart && dayStart <= eventEnd;
};

/**
 * Format month and year for display
 * @param {Date} date - Date to format
 * @param {Object} options - Formatter options
 * @param {string} options.locale - Locale for Intl formatting
 * @param {string} options.timeZone - Optional timezone
 * @returns {Object} Formatted month and year
 */
export const formatMonthYear = (date, { locale, timeZone } = {}) => {
  if (!date || !isValid(date)) {
    return {
      month: '',
      year: '',
      full: '',
    };
  }

  const localeCode = resolveLocale(locale);
  const safeTimeZone = resolveTimeZone(timeZone);

  const month = formatWithIntl(
    date,
    localeCode,
    { month: 'long' },
    safeTimeZone
  );
  const year = formatWithIntl(
    date,
    localeCode,
    { year: 'numeric' },
    safeTimeZone
  );
  const full = formatWithIntl(
    date,
    localeCode,
    { month: 'long', year: 'numeric' },
    safeTimeZone
  );

  return { month, year, full };
};

/**
 * Format date for display in list view headers
 * @param {Date} date - Date to format
 * @param {Object} options - Formatter options
 * @param {string} options.locale - Locale for Intl formatting
 * @param {string} options.timeZone - Optional timezone
 * @param {Date} options.now - Optional reference date for deterministic tests
 * @param {Function} options.t - Translation function
 * @returns {string} Formatted date string
 */
export const formatDateHeader = (
  date,
  { locale, timeZone, now = new Date(), t } = {}
) => {
  if (!date || !isValid(date)) {
    return '';
  }

  const localeCode = resolveLocale(locale);
  const safeTimeZone = resolveTimeZone(timeZone);
  const nowDate = coerceDateValue(now) || new Date();

  const formattedDate = formatWithIntl(
    date,
    localeCode,
    { weekday: 'long', month: 'long', day: 'numeric' },
    safeTimeZone
  );

  const todayKey = buildDateKey(nowDate, { locale: localeCode, timeZone: safeTimeZone });
  const targetKey = buildDateKey(date, { locale: localeCode, timeZone: safeTimeZone });
  const tomorrowKey = buildDateKey(addDays(nowDate, 1), {
    locale: localeCode,
    timeZone: safeTimeZone,
  });
  const yesterdayKey = buildDateKey(addDays(nowDate, -1), {
    locale: localeCode,
    timeZone: safeTimeZone,
  });

  if (targetKey === todayKey) {
    return `${translate(t, 'records.time.today', {}, 'Today')} • ${formattedDate}`;
  }

  if (targetKey === tomorrowKey) {
    return `${translate(t, 'records.time.tomorrow', {}, 'Tomorrow')} • ${formattedDate}`;
  }

  if (targetKey === yesterdayKey) {
    return `${translate(t, 'records.time.yesterday', {}, 'Yesterday')} • ${formattedDate}`;
  }

  return formattedDate;
};

/**
 * Format time for record display
 * @param {Object} record - Record with date attributes
 * @param {Object} options - Formatter options
 * @param {string} options.locale - Locale for Intl formatting
 * @param {string} options.timeZone - Optional timezone fallback
 * @param {Function} options.t - Translation function
 * @returns {string} Formatted time or localized all-day label
 */
export const formatRecordTime = (record, { locale, timeZone, t } = {}) => {
  if (!record?.attributes?.date) return '';

  const localeCode = resolveLocale(locale);
  const recordTimeZone = resolveTimeZone(record.attributes.date.timezone);
  const fallbackTimeZone = resolveTimeZone(timeZone);
  const effectiveTimeZone = recordTimeZone || fallbackTimeZone;

  if (record.attributes.date.isAllDay) {
    return translate(t, 'records.time.all_day', {}, 'All-day');
  }

  const startDate = toDate(record.attributes.date.start);
  if (!startDate) return '';

  const endDate = record.attributes.date.end ? toDate(record.attributes.date.end) : null;

  if (!endDate || isSameDayInZone(startDate, endDate, {
    locale: localeCode,
    timeZone: effectiveTimeZone,
  })) {
    return formatWithIntl(
      startDate,
      localeCode,
      { hour: 'numeric', minute: '2-digit' },
      effectiveTimeZone
    );
  }

  return `${formatWithIntl(
    startDate,
    localeCode,
    {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
    effectiveTimeZone
  )} - ${formatWithIntl(
    endDate,
    localeCode,
    {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
    effectiveTimeZone
  )}`;
};

/**
 * Navigate to next/previous month
 * @param {Date} currentMonth - Current month date
 * @param {number} direction - 1 for next, -1 for previous
 * @returns {Date} New month date
 */
export const navigateMonth = (currentMonth, direction) => {
  return direction > 0 ? addMonths(currentMonth, 1) : subMonths(currentMonth, 1);
};

/**
 * Get week day names
 * @param {Object} options - Formatter options
 * @param {string} options.locale - Locale for Intl formatting
 * @param {string} options.timeZone - Optional timezone
 * @returns {Array<string>} Array of abbreviated weekday names
 */
export const getWeekdayNames = ({ locale, timeZone } = {}) => {
  const localeCode = resolveLocale(locale);
  const safeTimeZone = resolveTimeZone(timeZone);
  const formatter = new Intl.DateTimeFormat(localeCode, {
    weekday: 'short',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  });
  const detector = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  });
  const probeDate = new Date(Date.UTC(2025, 0, 5, 12, 0, 0));
  const probeWeekday = detector.format(probeDate);
  const probeIndex =
    Object.prototype.hasOwnProperty.call(EN_WEEKDAY_TO_INDEX, probeWeekday)
      ? EN_WEEKDAY_TO_INDEX[probeWeekday]
      : 0;
  const sundayBase = new Date(probeDate.getTime() - probeIndex * DAY_IN_MS);

  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(sundayBase.getTime() + index * DAY_IN_MS))
  );
};

/**
 * Check if date is in the current displayed month
 * @param {Date} date - Date to check
 * @param {Date} currentMonth - Current month being displayed
 * @returns {boolean} True if date is in current month
 */
export const isCurrentMonth = (date, currentMonth) => {
  return isSameMonth(date, currentMonth);
};

/**
 * Sort records by date
 * @param {Array} records - Array of records to sort
 * @returns {Array} Sorted records
 */
export const sortRecordsByDate = (records) => {
  return [...records].sort((a, b) => {
    const dateA = getRecordDisplayDate(a);
    const dateB = getRecordDisplayDate(b);
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return dateA.getTime() - dateB.getTime();
  });
};

// Re-export commonly used date-fns functions
export { isSameDay };
